/**
 * MKS-162 — task-to-task link query/mutation hooks.
 *
 * Invalidation mirrors `useTaskLabels.ts`'s pattern: invalidate the query's
 * own key AND `taskActivityQueryKey(taskId)` — link create/delete now write
 * audit rows the activity timeline reads.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listTaskLinks,
  createTaskLink,
  deleteTaskLink,
  type TaskLinkRecord,
  type TaskLinkKind,
} from '../services/api/taskLinks';
import { taskActivityQueryKey } from './useTaskActivity';

export const taskLinksQueryKey = (taskId: number) => ['task-links', taskId] as const;

export function useTaskLinks(taskId: number | null | undefined) {
  return useQuery<TaskLinkRecord[]>({
    queryKey: taskId != null ? taskLinksQueryKey(taskId) : ['task-links', 'noop'],
    queryFn: () => listTaskLinks(taskId as number),
    enabled: taskId != null,
  });
}

export interface CreateTaskLinkInput {
  taskId: number;
  toTaskId: number;
  kind: TaskLinkKind;
}

export function useCreateTaskLink() {
  const queryClient = useQueryClient();
  return useMutation<TaskLinkRecord, Error, CreateTaskLinkInput>({
    mutationFn: (input) => createTaskLink(input.taskId, { toTaskId: input.toTaskId, kind: input.kind }),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: taskLinksQueryKey(input.taskId) });
      queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(input.taskId) });
    },
  });
}

export interface DeleteTaskLinkInput {
  taskId: number;
  linkId: number;
}

export function useDeleteTaskLink() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteTaskLinkInput>({
    mutationFn: (input) => deleteTaskLink(input.taskId, input.linkId),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: taskLinksQueryKey(input.taskId) });
      queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(input.taskId) });
    },
  });
}
